import { useState, useEffect } from "react";
import { useLang } from "../LangContext";
import { formatEur, daysUntil } from "../utils";
import { supabase } from "../supabase";

export default function ContractorHome({
  project, profile, members, timesheets, reserves, supplies, journal,
  todos, setTab, updateTimesheets, updateReserves, updateSupplies,
  uploadReservePhoto, updateJournal, showToast,
}) {
  const { t } = useLang();
  const myMember = members.find(m => m.user_id === profile.id);
  const myLots = myMember?.lots || [];

  const [localToast, setLocalToast] = useState(null);
  const notify = (msg) => {
    if (typeof showToast === "function") { showToast(msg); return; }
    setLocalToast(msg);
    setTimeout(() => setLocalToast(null), 2500);
  };

  // ── Quick action modals ──────────────────────────────
  const [modal, setModal] = useState(null); // 'signal' | 'hours' | 'supply'

  // Signal (réserve rapide)
  const [sigText, setSigText] = useState("");
  const [sigSev, setSigSev] = useState("medium");
  const [sigLoc, setSigLoc] = useState("");
  const [sigPhotos, setSigPhotos] = useState([]);
  const [sigUploading, setSigUploading] = useState(false);

  // Hours
  const [hEmployee, setHEmployee] = useState(profile.name);
  const [hHours, setHHours] = useState("");
  const [hDate, setHDate] = useState(new Date().toISOString().slice(0, 10));
  const [hLot, setHLot] = useState(myLots[0] || "");

  // Supply
  const [supDesc, setSupDesc] = useState("");
  const [supAmt, setSupAmt] = useState("");

  // ── Notifications (nouveautés depuis dernière visite) ──
  const storageKey = `travea_last_visit_${project.id}_${profile.id}`;
  const [newEntries, setNewEntries] = useState([]);
  const [showNewEntries, setShowNewEntries] = useState(false);

  useEffect(() => {
    const lastVisit = localStorage.getItem(storageKey);
    if (lastVisit && journal && journal.length > 0) {
      const fresh = journal.filter(e => {
        // entries have no ISO timestamp — use id (Date.now()) as proxy
        return e.id > parseInt(lastVisit);
      });
      setNewEntries(fresh);
    }
    localStorage.setItem(storageKey, Date.now().toString());
  }, []); // eslint-disable-line

  // ── Stats ────────────────────────────────────────────
  const openReserves = (reserves || []).filter(r => !r.resolved).length;
  const oneWeekAgo = new Date();
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
  const hoursThisWeek = (timesheets || [])
    .filter(t => new Date(t.date) >= oneWeekAgo)
    .reduce((s, t) => s + (t.hours || 0), 0);
  const pendingSupplies = (supplies || []).filter(s => s.status === "pending").length;

  // ── Mes actions (assignées à moi par l'architecte) ──
  const myTodos = Object.values(todos || {}).flat()
    .filter(t => t.assignee === "contractor" && !t.done)
    .sort((a, b) => {
      const da = a.deadline ? daysUntil(a.deadline) : 999;
      const db = b.deadline ? daysUntil(b.deadline) : 999;
      return da - db;
    });

  const todayLabel = new Date().toLocaleDateString("fr-FR", {
    weekday: "long", day: "numeric", month: "long",
  });

  const sevColor = s => s === "high" ? "#FC8181" : s === "medium" ? "#F6AD55" : "#A8C97E";
  const sevLabel = s => s === "high" ? "Bloquant" : s === "medium" ? "Modérée" : "Mineure";

  const closeModal = () => {
    setModal(null);
    setSigText(""); setSigSev("medium"); setSigLoc(""); setSigPhotos([]);
    setHHours(""); setHLot(myLots[0] || "");
    setSupDesc(""); setSupAmt("");
  };

  const submitSignal = async () => {
    if (!sigText.trim()) return;
    const entry = {
      id: Date.now(), severity: sigSev,
      text: sigText.trim(), location: sigLoc || null,
      date: new Date().toLocaleDateString("fr-FR"),
      resolved: false, photos: sigPhotos,
    };

    // Fresh read to avoid stale-closure overwrite
    const { data: reserveRow } = await supabase
      .from("project_data")
      .select("data")
      .eq("project_id", project.id)
      .eq("section", "reserves")
      .single();

    const existingEntries = reserveRow?.data?.entries ?? reserves;
    const newReserves = [entry, ...existingEntries];

    const { error } = await supabase
      .from("project_data")
      .upsert(
        { project_id: project.id, section: "reserves", data: { entries: newReserves }, updated_at: new Date().toISOString() },
        { onConflict: "project_id,section" }
      );

    if (error) { notify("Erreur lors de l'enregistrement"); return; }

    // Sync local React state (saveSection call inside is idempotent)
    updateReserves(newReserves);
    updateJournal([{
      id: Date.now() + 1, role: "contractor",
      authorName: profile.name, time: "À l'instant",
      text: `🚨 Réserve signalée : ${sigText.trim()}${sigLoc ? ` (${sigLoc})` : ""}`,
      type: "message", photos: sigPhotos.length > 0 ? sigPhotos : null,
    }, ...journal]);

    notify(t("signalSent"));
    closeModal();
  };

  const submitHours = () => {
    if (!hEmployee.trim() || !hHours) return;
    updateTimesheets([...timesheets, {
      id: Date.now(), employee: hEmployee.trim(),
      hours: parseFloat(hHours), date: hDate, lot: hLot || null,
    }]);
    notify(t("hoursSaved"));
    closeModal();
  };

  const submitSupply = () => {
    if (!supDesc.trim()) return;
    updateSupplies([...supplies, {
      id: Date.now(), description: supDesc.trim(),
      amount: parseFloat(supAmt) || 0,
      date: new Date().toLocaleDateString("fr-FR"),
      status: "pending",
    }]);
    notify(t("requestSent"));
    closeModal();
  };

  return (
    <div style={{ paddingBottom: 80, position: "relative" }}>
      {localToast && (
        <div style={{ position: "fixed", bottom: 90, left: "50%", transform: "translateX(-50%)", background: "#C9A96E", color: "#0F0E0C", borderRadius: 10, padding: "10px 20px", fontSize: 14, fontWeight: 600, zIndex: 9999, pointerEvents: "none" }}>
          {localToast}
        </div>
      )}

      {/* ── Greeting ── */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 12, color: "#7A7468", marginBottom: 4, textTransform: "capitalize" }}>{todayLabel}</div>
        <h1 style={{ fontFamily: "'DM Serif Display',serif", fontSize: 26, color: "#E8E2D8", margin: "0 0 3px", lineHeight: 1.15 }}>
          Bonjour, {profile.name.split(" ")[0]} 👷
        </h1>
        <div style={{ fontSize: 12, color: "#7A7468" }}>{project.name}{project.address && ` · 📍 ${project.address}`}</div>
      </div>

      {/* ── Nouveautés depuis dernière visite ── */}
      {newEntries.length > 0 && (
        <div className="card" style={{ padding: 14, marginBottom: 16, border: "1px solid #7EB8C930", background: "#7EB8C908", cursor: "pointer" }}
          onClick={() => setShowNewEntries(p => !p)}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#7EB8C9" }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: "#7EB8C9" }}>
                {newEntries.length} nouveau{newEntries.length > 1 ? "x" : ""} message{newEntries.length > 1 ? "s" : ""} depuis votre dernière visite
              </span>
            </div>
            <span style={{ fontSize: 11, color: "#7EB8C9" }}>{showNewEntries ? "▲" : "▼"}</span>
          </div>
          {showNewEntries && (
            <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
              {newEntries.slice(0, 5).map(e => (
                <div key={e.id} style={{ padding: "8px 10px", background: "#1A1916", borderRadius: 8, fontSize: 12 }}>
                  <div style={{ color: "#7A7468", marginBottom: 2, fontSize: 10 }}>{e.authorName}</div>
                  <div style={{ color: "#E8E2D8" }}>{(e.content || e.text || "").substring(0, 80)}{(e.content || e.text || "").length > 80 ? "..." : ""}</div>
                </div>
              ))}
              <button className="btn" onClick={e => { e.stopPropagation(); setTab("journal"); }}
                style={{ fontSize: 12, color: "#7EB8C9", textAlign: "center", padding: "6px 0" }}>
                Voir dans le journal →
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Ce qu'on attend de moi ── */}
      <div className="card" style={{ padding: 16, marginBottom: 16, border: myTodos.length > 0 ? "1px solid #C9A96E30" : "1px solid #2A2924" }}>
        <div style={{ fontSize: 11, color: "#7A7468", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 12 }}>
          Ce qu'on attend de moi
        </div>
        {myTodos.length === 0 && pendingSupplies === 0 && openReserves === 0 ? (
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0" }}>
            <span style={{ fontSize: 20 }}>✅</span>
            <span style={{ fontSize: 13, color: "#A8C97E" }}>{t("nothingToday")}</span>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {myTodos.slice(0, 4).map(todo => {
              const days = todo.deadline ? daysUntil(todo.deadline) : null;
              const dlColor = days === null ? "#5A5450" : days < 0 ? "#FC8181" : days <= 3 ? "#FC8181" : days <= 7 ? "#F6AD55" : "#A8C97E";
              const dlLabel = days === null ? "" : days < 0 ? `${Math.abs(days)}j retard` : days === 0 ? "Aujourd'hui" : `J-${days}`;
              return (
                <div key={todo.id} style={{ padding: "10px 12px", background: "#252420", borderRadius: 9, display: "flex", gap: 10, alignItems: "center", borderLeft: `3px solid ${dlColor}` }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, color: "#E8E2D8", marginBottom: todo.impact ? 3 : 0 }}>{todo.text}</div>
                    {todo.impact && <div style={{ fontSize: 11, color: "#F6AD55" }}>⚠ {todo.impact}</div>}
                  </div>
                  {dlLabel && <span style={{ fontSize: 11, fontWeight: 700, color: dlColor, flexShrink: 0 }}>{dlLabel}</span>}
                </div>
              );
            })}
            {myTodos.length > 4 && (
              <button className="btn" onClick={() => setTab("todos")} style={{ fontSize: 12, color: "#C9A96E", textAlign: "center", padding: "6px 0" }}>
                +{myTodos.length - 4} autres actions →
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── Stats ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginBottom: 16 }}>
        {[
          { label: "Réserves\nouvertes", value: openReserves, color: openReserves > 0 ? "#F6AD55" : "#A8C97E", onClick: () => setTab("mon-chantier") },
          { label: "Heures\ncette semaine", value: `${hoursThisWeek}h`, color: "#7EB8C9", onClick: () => setModal("hours") },
          { label: "Appros\nen attente", value: pendingSupplies, color: pendingSupplies > 0 ? "#F6AD55" : "#5A5450", onClick: () => setModal("supply") },
        ].map((s, i) => (
          <div key={i} className="card" onClick={s.onClick} style={{ padding: "14px 10px", textAlign: "center", cursor: "pointer" }}>
            <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: 26, color: s.color, marginBottom: 5 }}>{s.value}</div>
            <div style={{ fontSize: 9.5, color: "#5A5450", lineHeight: 1.4, whiteSpace: "pre-line" }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* ── Mes lots ── */}
      {myLots.length > 0 && (
        <div className="card" style={{ padding: 14, marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: "#7A7468", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 10 }}>{t("myLots")}</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
            {myLots.map((lot, i) => (
              <span key={i} style={{ background: "#7EB8C918", border: "1px solid #7EB8C940", borderRadius: 8, padding: "5px 13px", fontSize: 12, color: "#7EB8C9", fontWeight: 500 }}>● {lot}</span>
            ))}
          </div>
        </div>
      )}

      {/* ── Actions rapides ── */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, color: "#7A7468", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 12 }}>{t("quickActions")}</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {[
            { icon: "⏱", label: t("logHours"), color: "#7EB8C9", action: () => setModal("hours") },
            { icon: "📦", label: t("supplyRequest"), color: "#A8C97E", action: () => setModal("supply") },
            { icon: "📋", label: t("fullProject"), color: "#C9A96E", action: () => setTab("mon-chantier") },
            { icon: "💬", label: t("projectJournal"), color: "#7A7468", action: () => setTab("journal") },
          ].map((a, i) => (
            <button key={i} className="btn" onClick={a.action}
              style={{ padding: "16px 14px", borderRadius: 12, background: `${a.color}12`, border: `1px solid ${a.color}30`, textAlign: "left", display: "flex", flexDirection: "column", gap: 7 }}>
              <span style={{ fontSize: 20 }}>{a.icon}</span>
              <span style={{ fontSize: 12, color: a.color, fontWeight: 600, lineHeight: 1.3 }}>{a.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── FAB Signaler un problème ── */}
      <button onClick={() => setModal("signal")}
        style={{ position: "fixed", bottom: 80, right: 20, zIndex: 100, width: 60, height: 60, borderRadius: "50%", background: "#FC8181", border: "none", boxShadow: "0 4px 20px #FC818160", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 1, cursor: "pointer" }}>
        <span style={{ fontSize: 22 }}>🚨</span>
      </button>

      {/* ════════════════════════════════════════════
          MODALES
      ════════════════════════════════════════════ */}

      {/* MODAL: Signaler un problème */}
      {modal === "signal" && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="sheet" onClick={e => e.stopPropagation()} style={{ maxHeight: "90vh", overflowY: "auto" }}>
            <div style={{ padding: "16px 20px", borderBottom: "1px solid #2A2924", display: "flex", justifyContent: "space-between", alignItems: "center", position: "sticky", top: 0, background: "#1A1916", zIndex: 1 }}>
              <span style={{ fontFamily: "'DM Serif Display',serif", fontSize: 18, color: "#FC8181" }}>{t("signalProblem")}</span>
              <button className="btn" onClick={closeModal} style={{ fontSize: 18, color: "#5A5450" }}>✕</button>
            </div>
            <div style={{ padding: "20px", display: "flex", flexDirection: "column", gap: 12 }}>

              {/* Photo en premier — action principale */}
              <label style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, padding: "24px 16px", background: sigPhotos.length > 0 ? "#7EB8C910" : "#FC818110", border: `2px dashed ${sigPhotos.length > 0 ? "#7EB8C960" : "#FC818150"}`, borderRadius: 12, cursor: sigUploading ? "default" : "pointer" }}>
                {sigUploading ? (
                  <div style={{ fontSize: 13, color: "#7A7468" }}>Envoi en cours...</div>
                ) : sigPhotos.length > 0 ? (
                  <>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "center" }}>
                      {sigPhotos.map((url, i) => (
                        <div key={i} style={{ position: "relative" }}>
                          <img src={url} alt="" style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 8, border: "1px solid #2A2924" }} />
                          <button className="btn" onClick={ev => { ev.preventDefault(); setSigPhotos(prev => prev.filter((_, j) => j !== i)); }}
                            style={{ position: "absolute", top: -4, right: -4, width: 18, height: 18, borderRadius: "50%", background: "#FC8181", color: "#0F0E0C", fontSize: 10, display: "flex", alignItems: "center", justifyContent: "center", border: "none", padding: 0 }}>✕</button>
                        </div>
                      ))}
                    </div>
                    <span style={{ fontSize: 12, color: "#7EB8C9" }}>{t("addMorePhotos")}</span>
                  </>
                ) : (
                  <>
                    <span style={{ fontSize: 36 }}>📷</span>
                    <span style={{ fontSize: 14, color: "#FC8181", fontWeight: 600 }}>Prendre une photo</span>
                    <span style={{ fontSize: 11, color: "#5A5450" }}>Appuyez ici pour ouvrir l'appareil photo</span>
                  </>
                )}
                <input type="file" accept="image/*" multiple capture="environment" disabled={sigUploading} style={{ display: "none" }}
                  onChange={async (e) => {
                    const files = Array.from(e.target.files);
                    setSigUploading(true);
                    try {
                      for (const f of files) {
                        const url = await uploadReservePhoto(f);
                        if (url) setSigPhotos(prev => [...prev, url]);
                      }
                    } finally {
                      setSigUploading(false);
                    }
                  }} />
              </label>

              <textarea rows={3} value={sigText} onChange={e => setSigText(e.target.value)}
                placeholder={t("describeIssue")}
                style={{ resize: "none", fontSize: 14, padding: 12 }} autoFocus />

              <input value={sigLoc} onChange={e => setSigLoc(e.target.value)}
                placeholder={t("location")} style={{ fontSize: 13 }} />

              <div style={{ display: "flex", gap: 8 }}>
                {[{ v: "low", l: t("minor") }, { v: "medium", l: t("moderate") }, { v: "high", l: t("blocking") }].map(s => (
                  <button key={s.v} onClick={() => setSigSev(s.v)} className="btn"
                    style={{ flex: 1, padding: "8px 4px", borderRadius: 8, fontSize: 11, fontWeight: sigSev === s.v ? 700 : 400, background: sigSev === s.v ? `${sevColor(s.v)}25` : "#252420", border: `1px solid ${sigSev === s.v ? sevColor(s.v) : "#2A2924"}`, color: sigSev === s.v ? sevColor(s.v) : "#7A7468" }}>
                    {s.l}
                  </button>
                ))}
              </div>

              <button onClick={submitSignal} disabled={!sigText.trim()}
                style={{ background: sigText.trim() ? "#FC8181" : "#252420", color: sigText.trim() ? "#0F0E0C" : "#5A5450", borderRadius: 10, padding: 14, fontSize: 15, fontWeight: 700, border: "none", cursor: sigText.trim() ? "pointer" : "default" }}>
                Envoyer le signalement →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Pointer les heures */}
      {modal === "hours" && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="sheet" onClick={e => e.stopPropagation()}>
            <div style={{ padding: "16px 20px", borderBottom: "1px solid #2A2924", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontFamily: "'DM Serif Display',serif", fontSize: 18, color: "#7EB8C9" }}>{t("logHoursTitle")}</span>
              <button className="btn" onClick={closeModal} style={{ fontSize: 18, color: "#5A5450" }}>✕</button>
            </div>
            <div style={{ padding: "20px", display: "flex", flexDirection: "column", gap: 12 }}>
              <div><div className="sl" style={{ marginBottom: 6 }}>{t("employee")}</div>
                <input value={hEmployee} onChange={e => setHEmployee(e.target.value)} placeholder="Nom" style={{ fontSize: 14 }} /></div>
              <div style={{ display: "flex", gap: 10 }}>
                <div style={{ flex: 1 }}><div className="sl" style={{ marginBottom: 6 }}>{t("hours")}</div>
                  <input type="number" value={hHours} onChange={e => setHHours(e.target.value)} placeholder="0" style={{ fontSize: 20, fontWeight: 700, textAlign: "center" }} autoFocus /></div>
                <div style={{ flex: 1 }}><div className="sl" style={{ marginBottom: 6 }}>{t("date")}</div>
                  <input type="date" value={hDate} onChange={e => setHDate(e.target.value)} style={{ fontSize: 13 }} /></div>
              </div>
              {myLots.length > 0 && (
                <div><div className="sl" style={{ marginBottom: 8 }}>{t("lot")}</div>
                  <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
                    {myLots.map(lot => (
                      <button key={lot} onClick={() => setHLot(lot)} className="btn"
                        style={{ padding: "6px 14px", borderRadius: 8, fontSize: 12, fontWeight: hLot === lot ? 600 : 400, background: hLot === lot ? "#7EB8C920" : "#252420", border: `1px solid ${hLot === lot ? "#7EB8C960" : "#2A2924"}`, color: hLot === lot ? "#7EB8C9" : "#7A7468" }}>
                        {lot}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <button onClick={submitHours} disabled={!hHours || !hEmployee.trim()}
                style={{ background: hHours && hEmployee.trim() ? "#7EB8C9" : "#252420", color: hHours && hEmployee.trim() ? "#0F0E0C" : "#5A5450", borderRadius: 10, padding: 13, fontSize: 14, fontWeight: 700, border: "none" }}>
                Valider le pointage →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Demande d'approvisionnement */}
      {modal === "supply" && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="sheet" onClick={e => e.stopPropagation()}>
            <div style={{ padding: "16px 20px", borderBottom: "1px solid #2A2924", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontFamily: "'DM Serif Display',serif", fontSize: 18, color: "#A8C97E" }}>{t("supplyTitle")}</span>
              <button className="btn" onClick={closeModal} style={{ fontSize: 18, color: "#5A5450" }}>✕</button>
            </div>
            <div style={{ padding: "20px", display: "flex", flexDirection: "column", gap: 12 }}>
              <textarea rows={3} value={supDesc} onChange={e => setSupDesc(e.target.value)}
                placeholder={t("supplyPlaceholder")} style={{ resize: "none", fontSize: 14 }} autoFocus />
              <div><div className="sl" style={{ marginBottom: 6 }}>{t("estimatedAmount")}</div>
                <input type="number" value={supAmt} onChange={e => setSupAmt(e.target.value)} placeholder="0 €" style={{ fontSize: 16, fontWeight: 600 }} /></div>
              <button onClick={submitSupply} disabled={!supDesc.trim()}
                style={{ background: supDesc.trim() ? "#A8C97E" : "#252420", color: supDesc.trim() ? "#0F0E0C" : "#5A5450", borderRadius: 10, padding: 13, fontSize: 14, fontWeight: 700, border: "none" }}>
                Envoyer la demande →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
